import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import {
    SUB_PRODUCT_CATEGORY_FILTERS,
    getUnselectableReason,
    toggleSelection,
    setSelectionQty,
    removeSelection,
    buildSubProductEntry,
} from '../../lib/subProductPicker';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

function isSelected(selected, product) {
    return selected.some(item => String(item.id) === String(product.id));
}

/**
 * Multi-select picker for the product `subProducts` field.
 *
 * Opened by Input.openAddArrayObject (fields.js: drawerName 'addSubProducts')
 * with the same props as the generic addArrayObject drawer. Every ticked
 * product is handed to the `after` prop (Input.addArrayObject) one at a time,
 * so md5/date stamping, the admin vs. normal PATCH and the local-state update
 * stay exactly as in the single-pick flow.
 */
@observer
class AddSubProducts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            category: '',
            foundList: [],
            selected: [],
            isSearching: false,
            hasSearched: false,
            isAdding: false,
            addedCount: 0,
            addTotal: 0,
            addError: null,
        };
        this.searchTimer = null;
        this.searchSeq = 0;
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
    }

    runSearch = async ({ search, category }) => {
        const { holdingStore } = this.props;
        this.searchSeq += 1;
        const seq = this.searchSeq;
        this.setState({ isSearching: true });
        let results = [];
        try {
            results = await holdingStore.searchProducts(search, { category });
        } catch (err) {
            results = [];
        }
        if (seq !== this.searchSeq) {
            return; // A newer search superseded this one.
        }
        this.setState({ foundList: results || [], isSearching: false, hasSearched: true });
    }

    onSearchInput = (e) => {
        const search = e.target.value;
        const { category } = this.state;
        this.setState({ search });
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.runSearch({ search, category }), SEARCH_DEBOUNCE_MS);
    }

    onCategoryClick = (e) => {
        const { category = '' } = e.currentTarget.dataset;
        const { search } = this.state;
        this.setState({ category });
        clearTimeout(this.searchTimer);
        return this.runSearch({ search, category });
    }

    onToggleProduct = (e) => {
        const { idx } = e.currentTarget.dataset;
        const { object } = this.props;
        const { foundList, selected } = this.state;
        const product = foundList[parseInt(idx, 10)];
        if (!product || getUnselectableReason(object, product)) {
            return;
        }
        this.setState({ selected: toggleSelection(selected, product) });
    }

    onQtyInput = (e) => {
        const { id } = e.currentTarget.dataset;
        const { selected } = this.state;
        this.setState({ selected: setSelectionQty(selected, id, e.target.value) });
    }

    onRemoveSelected = (e) => {
        const { id } = e.currentTarget.dataset;
        const { selected } = this.state;
        this.setState({ selected: removeSelection(selected, id) });
    }

    onClickAdd = async () => {
        const { after = () => {}, drawerLevel } = this.props;
        const { appState } = this.props.stores;
        const { selected, isAdding } = this.state;
        if (isAdding || selected.length === 0) {
            return;
        }
        this.setState({
            isAdding: true, addedCount: 0, addTotal: selected.length, addError: null,
        });
        let remaining = [...selected];
        try {
            for (let i = 0; i < selected.length; i += 1) {
                // Sequential on purpose: each call PATCHes one $addToSet and
                // updates the shared product object before the next one reads it.
                // eslint-disable-next-line no-await-in-loop
                await after(buildSubProductEntry(selected[i]));
                remaining = removeSelection(remaining, selected[i].id);
                this.setState({ addedCount: i + 1, selected: remaining });
            }
        } catch (err) {
            this.setState({ isAdding: false, selected: remaining, addError: err?.message || 'error' });
            return;
        }
        this.setState({ isAdding: false, selected: [] });
        appState.toggleDrawer(false, drawerLevel);
    }

    render() {
        const { object = {} } = this.props;
        const {
            search, category, foundList, selected, isSearching, hasSearched,
            isAdding, addedCount, addTotal, addError,
        } = this.state;
        const canAdd = selected.length > 0 && !isAdding;
        const showNoResults = hasSearched && !isSearching && foundList.length === 0
            && (search.length >= MIN_SEARCH_LENGTH || !!category);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 20px;'>
                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                        <span>
                            <Text id='form.add'>Add</Text> <Text id='input.product-sub-title'>Sub Products</Text>
                        </span>
                        {object.name && <span class='text-body-secondary'>{object.name}</span>}
                    </div>
                    <div class='px-3 text-body-secondary small mb-2'>
                        <Text id='product.subProductPicker.hint'>Tick the products you want to add, then press Add.</Text>
                    </div>

                    <div class='px-3 mb-2'>
                        <div class='position-relative'>
                            <Localizer>
                                <input
                                    class='form-control form-control-lg'
                                    type='text'
                                    placeholder={<Text id='product.subProductPicker.search-placeholder'>Search for a product (min. 3 characters), or pick a category</Text>}
                                    onInput={this.onSearchInput}
                                    value={search}
                                    disabled={isAdding}
                                />
                            </Localizer>
                            {isSearching && (
                                <div class='position-absolute' style='right: 10px; top: 50%; transform: translateY(-50%);'>
                                    <div class='spinner-border spinner-border-sm text-primary' role='status'>
                                        <span class='visually-hidden'>Searching...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div class='px-3 mb-3 d-flex flex-row flex-wrap gap-1'>
                        {SUB_PRODUCT_CATEGORY_FILTERS.map(filter => (
                            <button
                                key={filter.value}
                                type='button'
                                class={`btn btn-sm rounded-pill ${category === filter.value ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                data-category={filter.value}
                                onClick={this.onCategoryClick}
                                disabled={isAdding}
                            >
                                <Text id={filter.textId}>{filter.text}</Text>
                            </button>
                        ))}
                    </div>

                    {selected.length > 0 && (
                        <div class='px-3 mb-3'>
                            <div class='text-body-secondary small text-uppercase mb-1'>
                                <Text id='product.subProductPicker.selected-title'>Selected products</Text> ({selected.length})
                            </div>
                            <div class='list-group'>
                                {selected.map(item => (
                                    <div key={item.id} class='list-group-item d-flex flex-row align-items-center gap-2 py-1'>
                                        <div class='flex-fill'>
                                            {item.name}
                                            <small class='text-body-secondary ms-1'>({item.externalId || item.productNumber || item.id})</small>
                                        </div>
                                        <Localizer>
                                            <input
                                                type='number'
                                                min='0'
                                                step='any'
                                                class='form-control form-control-sm'
                                                style='width: 90px; flex: 0 0 auto;'
                                                value={item.qty}
                                                data-id={item.id}
                                                onInput={this.onQtyInput}
                                                disabled={isAdding}
                                                aria-label={<Text id='product.subProductPicker.qty'>Quantity</Text>}
                                            />
                                        </Localizer>
                                        <Localizer>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-danger rounded-pill'
                                                data-id={item.id}
                                                onClick={this.onRemoveSelected}
                                                disabled={isAdding}
                                                title={<Text id='product.subProductPicker.remove'>Remove</Text>}
                                            >
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </Localizer>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {foundList.length > 0 && (
                        <div class='px-3 mb-3'>
                            <div class='text-body-secondary small text-uppercase mb-1'>
                                <Text id='form.search-result'>Search results</Text>
                            </div>
                            <div class='list-group' style='max-height: 40vh; overflow-y: auto;'>
                                {foundList.map((product, idx) => {
                                    const reason = getUnselectableReason(object, product);
                                    const checked = isSelected(selected, product);
                                    return (
                                        <label
                                            key={product.id ?? idx}
                                            class={`list-group-item d-flex flex-row align-items-center gap-2 py-1 ${reason ? 'text-body-tertiary' : ''}`}
                                            style={reason ? 'cursor: not-allowed;' : 'cursor: pointer;'}
                                        >
                                            <input
                                                class='form-check-input m-0 flex-shrink-0'
                                                type='checkbox'
                                                checked={checked}
                                                disabled={!!reason || isAdding}
                                                data-idx={idx}
                                                onChange={this.onToggleProduct}
                                            />
                                            <div class='flex-fill'>
                                                {product.name}
                                                <small class='text-body-secondary ms-1'>({product.externalId || product.productNumber || product.id})</small>
                                            </div>
                                            {reason === 'added' && (
                                                <span class='badge bg-body-secondary text-body-secondary'>
                                                    <Text id='product.subProductPicker.already-added'>Already added</Text>
                                                </span>
                                            )}
                                            {reason === 'self' && (
                                                <span class='badge bg-body-secondary text-body-secondary'>
                                                    <Text id='product.subProductPicker.this-product'>This product</Text>
                                                </span>
                                            )}
                                            {product.price != null && product.price !== '' && (
                                                <span class='text-body-secondary small'>{product.price}</span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {showNoResults && (
                        <div class='px-3 mb-3 text-body-secondary'>
                            <Text id='product.no-products'>No products found</Text>
                        </div>
                    )}

                    <div class='w-100 d-flex flex-column justify-content-start py-3 px-3'>
                        {addError && (
                            <div class='alert alert-danger py-2'>
                                <Text id='product.subProductPicker.add-error'>Something went wrong. Try again.</Text>
                            </div>
                        )}
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.onClickAdd} disabled={!canAdd}>
                            {isAdding ? (
                                <>
                                    <i class='fa-solid fa-spinner fa-spin me-2' />
                                    <Text id='product.subProductPicker.adding-progress' fields={{ done: addedCount, total: addTotal }}>
                                        Adding {addedCount} of {addTotal}...
                                    </Text>
                                </>
                            ) : (
                                <Text id='product.subProductPicker.add-count' fields={{ count: selected.length }}>
                                    Add {selected.length}
                                </Text>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default AddSubProducts;
